import api from '@api';
import * as utils from '@utils';

import { useEffect, useState } from 'react';
import type { AudienceOverviewTag } from 'types/common';
import type { Audience } from 'types/schema';
import Styled from './AudienceOverview.styled';

interface Props {
  audienceId: string;
  tags: AudienceOverviewTag[];
}

function AudienceOverview({ audienceId, tags }: Props) {
  const [audience, setAudience] = useState<Audience>();
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    setAudience(undefined);
    setError(false);
    setIsLoading(true);

    api.audiences
      .getAudienceDetails([audienceId])
      .then((audiences) => {
        setAudience(audiences[0]);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => setIsLoading(false));
  }, [audienceId]);

  const hasAllMetrics =
    audience?.sample != null &&
    audience?.universe != null &&
    audience?.percentage != null;

  if (isLoading) {
    return <Styled.Skeleton />;
  }

  if (error) {
    return (
      <Styled.ErrorText>Unable to load audience overview</Styled.ErrorText>
    );
  }

  return (
    <Styled.Card>
      <Styled.Header>
        <Styled.HeaderTitle>Audience interpretation</Styled.HeaderTitle>
        <Styled.HeaderSubtitle>
          Based on your prompt and context -- confirm before datapoints are
          selected
        </Styled.HeaderSubtitle>
      </Styled.Header>
      <Styled.Content>
        <Styled.AudienceTitle>{audience?.title}</Styled.AudienceTitle>
        <Styled.SupportingText>{audience?.description}</Styled.SupportingText>

        {tags.length > 0 && (
          <Styled.TagList>
            {tags.map((tag) => (
              <Styled.Tag key={`${tag.label}-${tag.value}`}>
                <Styled.TagLabel>{tag.label}</Styled.TagLabel>
                <Styled.TagValue>{tag.value}</Styled.TagValue>
              </Styled.Tag>
            ))}
          </Styled.TagList>
        )}

        {hasAllMetrics && (
          <Styled.SupportingText>
            Your audience includes{' '}
            <Styled.Highlight>{audience.sample}</Styled.Highlight> respondents,
            representing approximately{' '}
            <Styled.Highlight>
              {utils.formatLargeNumber(audience.universe)}
            </Styled.Highlight>{' '}
            real-world people and{' '}
            <Styled.Highlight>
              {audience.percentage.toFixed(1)}%
            </Styled.Highlight>{' '}
            of the population.
          </Styled.SupportingText>
        )}
      </Styled.Content>
    </Styled.Card>
  );
}

export default AudienceOverview;
